import { gql } from 'graphql-tag';
import { useQuery } from '@apollo/client';
import get from 'lodash.get';
import { useCallback, useEffect } from 'react';
import { useHasFeatureFlag, useIsMount } from '~/modules/common/hooks';
import { specificResourceAllocationFragment } from '~/modules/resourcing/common/fragments';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import { mapProjectStatusToProjectWorkflowStages } from './utils';

export const RESOURCE_ALLOCATIONS_QUERY_BY_USER = gql`
  query resourceAllocationsSummary(
    $dateRange: DateRangeInput!
    $periodResolution: PeriodResolutionOption!
    $userUri: String!
    $cursor: String
    $limit: Int
    $sort: ResourceAllocationSort
    $projectWorkflowStages: [ProjectWorkflowStage!]
  ) {
    resourceAllocationsSummary(
      userUri: $userUri
      dateRange: $dateRange
      periodResolution: $periodResolution
      cursor: $cursor
      limit: $limit
      sort: $sort
      projectWorkflowStages: $projectWorkflowStages
    ) {
      nextPageCursor
      resourceAllocationSummaryByDateRange {
        resourceAllocation {
          ...SpecificResourceAllocation
          project {
            id
            displayText
            startDate
            endDate
            permittedActionUris
            defaultScheduleRule {
              dateRange {
                startDate
                endDate
              }
              do
            }
          }
          role {
            id
            displayText
            uri
          }
        }
        resourceAllocatedHoursByDateRange {
          resourceAllocatedHours
          dateRange {
            startDate2
            endDate2
          }
        }
      }
    }
  }
  ${specificResourceAllocationFragment}
`;

export const QUERY_LIMIT = 5;

export const useResourceAllocationsSummary = ({
  scale,
  dateRange,
  cursor,
  limit = QUERY_LIMIT,
  userUri,
  sort,
  projectStatusFilterOption
}) => {
  const isResourceActualModeEnabled = useHasFeatureFlag({
    featureFlag: 'isResourceActualModeEnabled'
  });

  const isMounting = useIsMount();
  const periodResolution = mapScaleToPeriodResolution(scale);

  const { loading, data, fetchMore, refetch, error } = useQuery(
    RESOURCE_ALLOCATIONS_QUERY_BY_USER,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        sort,
        cursor,
        limit,
        dateRange,
        periodResolution,
        userUri,
        projectWorkflowStages: mapProjectStatusToProjectWorkflowStages(
          projectStatusFilterOption
        )
      }
    }
  );

  const resourceAllocationsSummary =
    get(data, 'resourceAllocationsSummary') || {};

  const {
    resourceAllocationSummaryByDateRange = [],
    nextPageCursor
  } = resourceAllocationsSummary;

  const loadMore = useLoadMoreCallback({
    variables: {
      sort,
      cursor,
      limit,
      dateRange,
      periodResolution,
      userUri
    },
    fetchMore,
    nextPageCursor,
    sort
  });

  useEffect(() => {
    if (!isMounting && isResourceActualModeEnabled) refetch();
  }, [refetch, isResourceActualModeEnabled, isMounting]);

  return {
    loadingAllocations: loading || false,
    loadMore,
    nextPageCursor,
    allocations: resourceAllocationSummaryByDateRange || [],
    refetch,
    error
  };
};

export const useLoadMoreCallback = ({ nextPageCursor, fetchMore, variables }) =>
  useCallback(async () => {
    if (!nextPageCursor) return;

    try {
      await fetchMore({
        variables: {
          ...variables,
          cursor: nextPageCursor
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            ...prev,
            resourceAllocationsSummary: {
              ...prev.resourceAllocationsSummary,
              resourceAllocationSummaryByDateRange: [
                ...prev.resourceAllocationsSummary
                  .resourceAllocationSummaryByDateRange,
                ...fetchMoreResult.resourceAllocationsSummary
                  .resourceAllocationSummaryByDateRange
              ],
              nextPageCursor:
                fetchMoreResult.resourceAllocationsSummary.nextPageCursor
            }
          };
        }
      });
      // eslint-disable-next-line no-empty
    } catch {}
  }, [nextPageCursor, fetchMore, variables]);

export default useResourceAllocationsSummary;
